import { FaShoppingCart, FaBars, FaTimes } from 'react-icons/fa'
import PopoverMenu from '../../shared/components/PopoverMenu'
import { useState } from 'react'
import CartModal from './CartModal'
import { useLocation } from 'react-router-dom'
import LanguageSwitchButton from './LanguageSwitchButton'
import CurrencySelect from './CurrencySelect'
import { FaMoneyBill } from 'react-icons/fa6'
import DialogModal from '../../shared/components/DialogModal'
import { useCurrency } from '../../util/CurrencyContext'
import { useCart } from '../../util/CartContext'
import { useTranslation } from 'react-i18next'
import LanguageCurrencyButton from './LanguageCurrencyButton'
import LanguageCurrencyModal from './LanguageCurrencyModal'

export default function Navbar() {
    const [isCartModalOpen, setIsCartModalOpen] = useState(false)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const location = useLocation()
    const pathname = location.pathname
    const { currency } = useCurrency()

    const { cart, isLoading: loading } = useCart()
    const cartCount = cart.items.length

    const { t } = useTranslation()
    const isPaymentSuccessPage = location.pathname === '/payment-success'

    const navMenus = [
        {
            buttonText: t('navigation.menuItems.news'),
            href: '/news',
        },
        {
            buttonText: t('navigation.menuItems.projects'),
            href: '/projects',
        },
        {
            buttonText: t('navigation.menuItems.religious_dues'),
            href: '/religious-dues',
        },
        {
            buttonText: t('navigation.menuItems.get_involved'),
            href: '/get-involved',
        },
        {
            buttonText: t('navigation.menuItems.about_us'),
            href: '/about-us',
        },
    ]

    return (
        <header className='sticky top-0 z-50 bg-white shadow-sm'>
            <nav className='relative grid [grid-template-columns:1fr_2fr_1fr] items-center h-16 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                {/* Left: Logo + Language */}
                <div className='flex items-center col-start-1 gap-4'>
                    <a href='/' className='text-lg font-bold text-yellow-700'>
                        <img src='/logo.jpeg' className='w-24 h-7' alt='logo of strive2help' />
                    </a>
                </div>

                {/* Center: Navigation (Always centered) */}
                <div className='hidden col-start-2 gap-6 md:flex justify-self-center'>
                    {navMenus.map((menu, idx) =>
                        menu.items ? (
                            <PopoverMenu
                                key={idx}
                                buttonText={menu.buttonText}
                                items={menu.items}
                                className={`text-gray-700 transition-colors hover:text-gray-900 ${
                                    pathname.startsWith(menu.href) ? 'font-bold' : ''
                                }`}
                            />
                        ) : (
                            <a
                                key={idx}
                                href={menu.href}
                                className={`text-gray-700 transition-colors hover:text-gray-900 ${
                                    pathname.startsWith(menu.href) ? 'font-bold' : ''
                                }`}
                            >
                                {menu.buttonText}
                            </a>
                        )
                    )}
                </div>

                {/* Right: Cart + Mobile Toggle */}
                <div className='flex items-center col-start-3 gap-4 justify-self-end'>
                    <div className='hidden md:inline-flex'>
                        <LanguageCurrencyButton onOpenModal={() => setIsModalOpen(true)} />
                    </div>

                    {isModalOpen && <LanguageCurrencyModal onClose={() => setIsModalOpen(false)} />}

                    {!isPaymentSuccessPage && (
                        <button
                            onClick={() => setIsCartModalOpen(true)}
                            className='text-gray-700 hover:text-gray-900'
                        >
                            <div className='relative flex items-center col-start-1 gap-4'>
                                <FaShoppingCart className='text-xl' />
                                {!loading && cartCount > 0 && (
                                    <span className='absolute bottom-4 left-4 w-2.5 h-2.5 bg-red-600 rounded-full border-2 border-white'></span>
                                )}
                            </div>
                        </button>
                    )}
                    <button
                        className='text-gray-700 md:hidden hover:text-gray-900'
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    >
                        {isMobileMenuOpen ? (
                            <FaTimes className='text-xl' />
                        ) : (
                            <FaBars className='text-xl' />
                        )}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className='block px-4 py-4 md:hidden'>
                    <div className='flex flex-col space-y-2'>
                        {/* Navigation links */}
                        {navMenus.map((menu, idx) =>
                            menu.items ? (
                                <PopoverMenu
                                    key={idx}
                                    buttonText={menu.buttonText}
                                    items={menu.items}
                                    mobile
                                    className={`text-gray-700 ${
                                        pathname.startsWith(menu.href) ? 'font-bold' : ''
                                    }`}
                                />
                            ) : (
                                <a
                                    key={idx}
                                    href={menu.href}
                                    className={`text-gray-700 ${
                                        pathname.startsWith(menu.href) ? 'font-bold' : ''
                                    }`}
                                >
                                    {menu.buttonText}
                                </a>
                            )
                        )}

                        {/* Divider */}
                        <hr className='my-3 border-t border-gray-200' />

                        {/* Language switcher */}
                        <div className='flex items-center gap-2'>
                            <button
                                className='py-0.5 px-2 inline-flex md:hidden items-center gap-2 rounded transition-all duration-100 ease-out focus:outline-none active:scale-95 hover:scale-[1.02] shadow-sm text-gray-900 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-yellow-500 w-20'
                                onClick={() => setIsModalOpen(!isModalOpen)}
                            >
                                <FaMoneyBill /> {currency}
                            </button>
                            <div className='inline-flex md:hidden'>
                                <LanguageSwitchButton />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cart Modal */}
            <CartModal isOpen={isCartModalOpen} onClose={() => setIsCartModalOpen(false)} />
        </header>
    )
}
